import {AttendanceAdjustmentApprovalAction} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {toast} from "react-toastify";
import {getToastMessageByStatus} from "../../../../../ApprovalTracker/Model/Model";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    errorMsg,
    getDateFormat,
    getTimeFormat
} from "../../../../../../../Apis/functions";
import i18next from 'i18next';
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import routes from "../../../../../../../Routes/routes";
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import {DEVICE_ACCESS_TYPE_MODEL} from "../../../../../Devices/Model/Model";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {IconEntryDevice, IconRightArrow, IconScheduleTable} from "../../../../../../../components/buttons/IconButtons";
import {POSITION_MODEL} from "../../../../../../../Helpers/PositionModel";
import React from "react";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Executes an approval action for an attendance adjustment request.
 *
 * - Sends approval/denial/change request to backend
 * - Displays success or error feedback
 * - Redirects user depending on resulting status
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigation function
 * @param {number|string} id - Request identifier
 * @param {string} comment - User comment or reason for action
 * @param {number} status - Approval status (STATUS_MODEL)
 * @param {Function} setModified - State setter to trigger refresh
 */
export const handleOnClick = (dispatch, navigate, id, comment, status, setModified) => {
    const body = {
        id,
        approvalStatusId: status,
        reason: comment
    };

    dispatch(AttendanceAdjustmentApprovalAction(body)).then(({ payload: { data: { success, code } } }) => {
        if (success) {
            setModified(true);
            toast.success(i18next.t(getToastMessageByStatus(status)));
            if(status !== STATUS_MODEL.CHANGES_REQUESTED)
                navigate(routes.attendance.main, {
                    state: {
                        currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                        currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY
                    }
                });
        } else {
            errorMsg(code);
        }
    });
}

/**
 * Model that maps submission request statuses to UI configuration.
 *
 * Includes:
 * - Labels
 * - Toast messages
 * - Styles (colors, classes)
 * - Translation keys
 */
export const SUBMISSION_REQUEST_CARD_MODEL = {
    APPROVED: {
        id: STATUS_MODEL.APPROVED,
        title : 'approved',
        toast: "approved_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "approved_by"
    },
    DENIED: {
        id: STATUS_MODEL.DENIED,
        title : 'denied',
        toast: "denied_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
        byTrans: "denied_by"
    },
    CHANGES_REQUESTED: {
        id: STATUS_MODEL.CHANGES_REQUESTED,
        title : 'changes_requested',
        toast: "changes_requested_by",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
        byTrans: "changes_requested_by"
    },
    /**
     * Retrieves configuration by status ID.
     *
     * @param {number|string} statusId - Status identifier
     * @returns {Object|null} Matching configuration or null if not found
     */
    getById: function (statusId) {
        const row = Object.entries(SUBMISSION_REQUEST_CARD_MODEL).find(([ key, data]) => data?.id === parseInt(statusId));
        return row?.[1] || null;
    }
}

/**
 * Maps grouped batch request items into table-ready rows.
 *
 * - Parses grouped JSON data
 * - Extracts entry and exit records
 * - Builds formatted row objects
 *
 * @param {Array} objectItems - Raw grouped request items
 * @returns {Array} Formatted rows for table rendering
 */
export const mapRequestedBatchRows = (objectItems) => {//Approbal view o request view
    return objectItems?.map((e, index) => {
        const group = JSON.parse(e[1]);
        const entry = group?.find(x => x.type === DEVICE_ACCESS_TYPE_MODEL.ENTRY.name);
        const exit = group?.find(x => x.type === DEVICE_ACCESS_TYPE_MODEL.EXIT.name);

        return buildBatchApprovalRows({
            index,
            fullName: entry?.fullName || exit?.fullName,
            departmentName: entry?.departmentName || exit?.departmentName,
            totalItems: objectItems?.length,
            shiftStart: entry?.shiftStart || exit?.shiftStart,
            shiftEnd: entry?.shiftEnd || exit?.shiftEnd,
            entryItemId: entry?.id,
            entryDateOriginal: entry?.dateOriginal,
            entryDateAdjusted: entry?.dateAdjusted,
            exitItemId: exit?.id,
            exitDateOriginal: exit?.dateOriginal,
            exitDateAdjusted: exit?.dateAdjusted
        });
    });
};

/**
 * Builds a normalized row object for batch approval table.
 *
 * - Formats dates and times
 * - Defines UI-related properties (column widths, classes)
 * - Handles missing values safely
 *
 * @param {Object} params - Row configuration
 * @returns {Object} Formatted row object
 */
export const buildBatchApprovalRows = ({
    index,
    fullName,
    departmentName,
    totalItems,
    shiftStart,
    shiftEnd,
    entryItemId,
    entryDateOriginal,
    entryDateAdjusted,
    exitItemId,
    exitDateOriginal,
    exitDateAdjusted,
}) => {
    const borderClass = index === totalItems - 1 ? "border-0" : '';
    let dateColumnWidth = "w-70px";
    let timeColumnWidth = "w-40px";
    if(!entryDateOriginal || !entryDateAdjusted || !exitDateOriginal || !exitDateAdjusted){
        dateColumnWidth = "w-79px";
        timeColumnWidth = "w-79px";
    }

    return {
        fullName: fullName,
        departmentName: departmentName,
        className: `${borderClass} w-20 align-top`,
        dateColumnWidth,
        timeColumnWidth,
        shiftStart: shiftStart ? getDateFormat(shiftStart, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds) : null,
        shiftEnd: shiftEnd ? getDateFormat(shiftEnd, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds) : null,
        entryItemId: entryItemId,
        entryDateOriginal: entryDateOriginal ? getDateFormat(entryDateOriginal, false) : null,
        entryFullAdjusted: entryDateAdjusted,
        entryDateAdjusted: entryDateAdjusted ? getDateFormat(entryDateAdjusted, false): null,
        entryTimeOriginal: entryDateOriginal ? getTimeFormat(entryDateOriginal, DATE_TIME_FORMAT_NAME_MODEL.HHmmss) : null,
        entryTimeAdjusted: entryDateAdjusted ? getTimeFormat(entryDateAdjusted, DATE_TIME_FORMAT_NAME_MODEL.HHmmss) : null,

        exitItemId: exitItemId,
        exitDateOriginal: exitDateOriginal ? getDateFormat(exitDateOriginal, false) : null,
        exitFullAdjusted: exitDateAdjusted,
        exitDateAdjusted: exitDateAdjusted ? getDateFormat(exitDateAdjusted, false): null,
        exitTimeOriginal: exitDateOriginal ? getTimeFormat(exitDateOriginal, DATE_TIME_FORMAT_NAME_MODEL.HHmmss) : null,
        exitTimeAdjusted: exitDateAdjusted ? getTimeFormat(exitDateAdjusted, DATE_TIME_FORMAT_NAME_MODEL.HHmmss) : null,
    };
}

/**
 * Defines table column configuration for batch approval view.
 *
 * - Includes custom renderers for:
 *   - Employee info
 *   - Date comparison (original vs adjusted)
 *   - Time comparison (entry/exit)
 *
 * @param {Function} t - Translation function
 * @returns {Array} Column configuration array
 */
export const requestedBatchApprovalColumns  = (t) => [
    {
        id: "name",
        header: t("name"),
        className: "w-20 align-top background-general-00",
        render: (id, row) => {
            const { fullName, departmentName} = row;
            return <div className='d-flex flex-column align-items-start'>
                <div className={'flex-grow-1 minw-0 mb-2'}>
                    <CustomTooltip
                        text={fullName}
                        classes={'font-color-general-950 fs-14px text-ellipsis-1-row text-capitalize'}
                        showIfTruncated={true}
                        placement={'top'}
                    />
                </div>
                { departmentName &&
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={departmentName}
                        variant={'compact'}
                    />
                }
            </div>
        }
    },
    {
        id: "scheduledShift",
        header: t("scheduled_shift"),
        className: "text-nowrap",
        render: (id, row) => {
            const { shiftStart, shiftEnd, entryDateAdjusted, exitDateAdjusted } = row;
            if (!shiftStart && !shiftEnd) return EMPTY_VALUES.DEFAULT;

            // Same wrapper as the date column, so each bound lines up with its
            // entry / exit counterpart.
            return <div className="d-flex flex-column justify-content-between h-102px">
                {/* The start of the shift only makes sense once the row has an entry. */}
                { entryDateAdjusted &&
                    <div className={'d-flex align-items-center'}>
                        <div><IconScheduleTable className={'mr-8px mb-1 icon-color-general-950'} /></div>
                        {t('entry')}: {shiftStart}
                    </div>
                }
                {/* The end of the shift only makes sense once the row has an exit. */}
                { exitDateAdjusted &&
                    <div className={'d-flex align-items-center'}>
                        <div><IconScheduleTable className={'mr-8px mb-1 icon-color-general-950'} /></div>
                        {t('exit')}: {shiftEnd}
                    </div>
                }
            </div>
        }
    },
    {
        id: "date",
        header: t("date"),
        render: (id, row) => {
            const {entryDateOriginal, entryDateAdjusted, exitDateOriginal, exitDateAdjusted, dateColumnWidth} = row;

            return  <div className="d-flex flex-column justify-content-between h-102px">

                {/* TOP (ENTRY) */}
                {entryDateAdjusted &&
                    <div className="d-flex align-items-center">
                        <span className={`font-color-general-950 fs-14px ${dateColumnWidth}`}>
                            { entryDateOriginal ? entryDateOriginal : t("no_record") }
                        </span>

                        <div className="px-2 flex-shrink-0">
                            <IconRightArrow />
                        </div>

                        <span className={`font-color-general-950 fs-14px ${dateColumnWidth}`}>
                           { entryDateAdjusted ? entryDateAdjusted : t("no_record") }
                        </span>
                    </div>
                }

                {/* BOTTOM (EXIT) */}
                {exitDateAdjusted &&
                    <div className="d-flex align-items-center minw-0">
                        <span className={`font-color-general-950 fs-14px ${dateColumnWidth}`}>
                           { exitDateOriginal ? exitDateOriginal : t("no_record") }
                        </span>
                        <div className="px-2 flex-shrink-0">
                            <IconRightArrow/>
                        </div>
                        <span className={`font-color-general-950 fs-14px ${dateColumnWidth}`}>
                           { exitDateAdjusted ? exitDateAdjusted : t("no_record") }
                        </span>
                    </div>
                }
            </div>
        }
    },
    {
        id: "attendance",
        header: t("attendance_captured"),
        render: (id, row) => {
            const { entryTimeOriginal, entryTimeAdjusted, exitTimeOriginal, exitTimeAdjusted, timeColumnWidth} = row;
            return <>
                { entryTimeAdjusted &&
                <>
                    <div className={"mb-3"}>
                        <CustomChip legend={t('entry')}
                                    fontColor={colors.success700}
                                    borderColor={colors.success100}
                                    background={colors.success100}
                                    classNameIcon={'icon-color-success-700 w-15px'}
                                    icon={IconEntryDevice}
                                    iconPosition={POSITION_MODEL.RIGHT}
                        />
                    </div>
                    <div className={"mb-3"}>
                        <div className={"d-flex justify-content-start"}>
                            <span className={`font-color-shades-500 fs-14px ${timeColumnWidth}`}>{ entryTimeOriginal ? entryTimeOriginal : t("no_record")}</span>
                            <div className={"px-4"}><IconRightArrow/></div>
                            <span className={`ibl-font-w-600 fs-14px ${timeColumnWidth}`}>{ entryTimeAdjusted ? entryTimeAdjusted : t("no_record")}</span>
                        </div>
                    </div>
                </>
                }

                { exitTimeAdjusted &&
                <>
                    <div className={"mb-3"}>
                        <CustomChip legend={t('exit')}
                                    fontColor={colors.errorCode700}
                                    borderColor={colors.errorCode100}
                                    background={colors.errorCode100}
                                    classNameIcon={'icon-color-error-code-700 w-15px'}
                                    icon={IconEntryDevice}
                                    iconPosition={POSITION_MODEL.RIGHT}
                        />
                    </div>
                    <div className={"d-flex justify-content-start"}>
                        <span className={`font-color-shades-500 fs-14px ${timeColumnWidth}`}>{ exitTimeOriginal ? exitTimeOriginal : t("no_record") }</span>
                        <div className={"px-4"}><IconRightArrow/></div>
                        <span className={`ibl-font-w-600 fs-14px ${timeColumnWidth}`}>{ exitTimeAdjusted ? exitTimeAdjusted : t("no_record")}</span>
                    </div>
                </>
                }
            </>
        }
    }
];
